import { FormlyFieldConfig } from '@ngx-formly/core';
import { TripettoCluster, TripettoNode } from '../utils/tripetto-interfaces';
import { ConversionWarning } from '@nx-ahs/formly/shared';
import { convertNode } from './node-converter';
import {
	generateWarning,
	generateLanguagesBlockWarning,
	generateSetterBlockWarning
} from '../utils/warning-generators';
import { convertBranchConditionsToHideExpression } from '../utils/condition-converter-v2';
import { NodeToFieldMap } from '../utils/node-field-map';

/**
 * PASS 1: Convert all nodes in clusters to Formly fields
 * Recursively processes all clusters and branches, converting nodes and registering in map
 */
export function convertAllNodesToFields(
	clusters: TripettoCluster[],
	blockTypeMapping: Record<string, string>,
	nodeFieldMap: NodeToFieldMap
): { fields: FormlyFieldConfig[]; warnings: ConversionWarning[]; unconverted: TripettoNode[] } {
	const allFields: FormlyFieldConfig[] = [];
	const allWarnings: ConversionWarning[] = [];
	const allUnconverted: TripettoNode[] = [];

	// Track used keys across all clusters for deduplication
	const usedKeys = new Set<string>();

	for (const cluster of clusters) {
		const result = convertClusterNodes(cluster, blockTypeMapping, nodeFieldMap, usedKeys);
		allFields.push(...result.fields);
		allWarnings.push(...result.warnings);
		allUnconverted.push(...result.unconverted);
	}

	return {
		fields: allFields,
		warnings: allWarnings,
		unconverted: allUnconverted
	};
}

/**
 * Convert nodes in a single cluster to Formly fields (Pass 1)
 */
function convertClusterNodes(
	cluster: TripettoCluster,
	blockTypeMapping: Record<string, string>,
	nodeFieldMap: NodeToFieldMap,
	usedKeys: Set<string>
): { fields: FormlyFieldConfig[]; warnings: ConversionWarning[]; unconverted: TripettoNode[] } {
	const fields: FormlyFieldConfig[] = [];
	const warnings: ConversionWarning[] = [];
	const unconverted: TripettoNode[] = [];

	// Build slot lookup for setter block warnings (Pass 1 only needs this for warnings)
	const slotLookup = new Map<string, { alias?: string; nodeName?: string; slotId?: string }>();
	for (const node of cluster.nodes || []) {
		for (const slot of node.slots || []) {
			slotLookup.set(slot.id, { alias: slot.alias, nodeName: node.name, slotId: slot.id });
		}
	}

	// Process each node in the cluster
	for (const node of cluster.nodes || []) {
		// Skip disabled nodes
		if (node.disabled) {
			warnings.push(
				generateWarning(
					'info',
					node.id,
					node.name || 'Unnamed Node',
					node.block?.type || 'unknown',
					'Node is disabled and was skipped'
				)
			);
			continue;
		}

		try {
			// Check if node has branches (complex conditional logic)
			if (node.branches && node.branches.length > 0) {
				warnings.push(
					generateWarning(
						'warning',
						node.id,
						node.name || 'Unnamed Node',
						node.block?.type || 'unknown',
						'Node has complex branching - manual review required',
						'Consider using hideExpression for simple conditions or restructuring the form',
						{ branchCount: node.branches.length }
					)
				);
			}

			// Convert node to Formly field (WITHOUT branch processing)
			const field = convertNode(node, blockTypeMapping, slotLookup);

			if (field) {
				// Deduplicate keys - if key already used, append numeric suffix
				const fieldKey = field.key as string;
				if (usedKeys.has(fieldKey)) {
					let suffix = 2;
					while (usedKeys.has(`${fieldKey}${suffix}`)) suffix++;
					field.key = `${fieldKey}${suffix}`;
				}
				usedKeys.add(field.key as string);

				fields.push(field);

				// Register node and slots in the map
				nodeFieldMap.registerField(node.id, field);

				// Register each slot with its field key
				for (const slot of node.slots || []) {
					const fieldKey = field.key as string;
					nodeFieldMap.registerSlot(slot.id, fieldKey, {
						alias: slot.alias,
						nodeName: node.name,
						nodeId: node.id
					});
				}

				// Check if options contain interpolated values (dynamic options)
				if (field.props?.options && Array.isArray(field.props.options)) {
					const hasInterpolation = field.props.options.some((option: any) => {
						const labelHasInterpolation = option.label && /@[a-f0-9]{64}/.test(option.label);
						const valueHasInterpolation = option.value && /@[a-f0-9]{64}/.test(option.value);
						return labelHasInterpolation || valueHasInterpolation;
					});

					if (hasInterpolation) {
						warnings.push(
							generateWarning(
								'warning',
								(field.key as string) || 'unknown',
								field.props?.label || field.props?.name || 'Unnamed Field',
								node.block?.type || 'unknown',
								'Options contain dynamic values (@slotId references)',
								`Formly does not natively support dynamic option labels that change based on other field values. The options have been imported with @slotId references intact, but they will display as literal text.\n\nTo implement dynamic options, you will need to:\n1. Use Formly expressions to dynamically update props.options based on model changes\n2. Create a custom field type that watches other fields and updates options\n3. Or restructure the form to avoid dynamic option labels`,
								{ hasInterpolation: true }
							)
						);
					}
				}

				// Generate warnings for special block types
				if (node.block?.type === 'ahs-component') {
					warnings.push(
						generateWarning(
							'warning',
							node.id,
							node.name || 'Unnamed Node',
							node.block.type,
							'Custom component block requires manual integration',
							'Register the custom component in Formly and update the field configuration'
						)
					);
				}

				if (node.block?.type === 'languages') {
					warnings.push(generateLanguagesBlockWarning(node));
				}

				if (node.block?.type === 'tripetto-block-setter' || node.block?.type === 'ahs-block-setter') {
					const warning = generateSetterBlockWarning(node, slotLookup);
					if (warning) {
						warnings.push(warning);
					}
				}
			} else {
				unconverted.push(node);
				warnings.push(
					generateWarning(
						'error',
						node.id,
						node.name || 'Unnamed Node',
						node.block?.type || 'unknown',
						'Failed to convert node - unsupported block type or missing data'
					)
				);
			}
		} catch (error) {
			unconverted.push(node);
			warnings.push(
				generateWarning(
					'error',
					node.id,
					node.name || 'Unnamed Node',
					node.block?.type || 'unknown',
					`Conversion failed: ${error.message}`
				)
			);
		}
	}

	// Recursively process branches (Pass 1: only convert nodes, don't apply conditions yet)
	if (cluster.branches && cluster.branches.length > 0) {
		for (const branch of cluster.branches) {
			for (const branchCluster of branch.clusters || []) {
				const branchResult = convertClusterNodes(branchCluster, blockTypeMapping, nodeFieldMap, usedKeys);
				fields.push(...branchResult.fields);
				warnings.push(...branchResult.warnings);
				unconverted.push(...branchResult.unconverted);
			}
		}
	}

	return { fields, warnings, unconverted };
}

/**
 * PASS 2: Apply branch conditions as hideExpression to fields
 * Recursively processes all clusters and branches, applying conditions using actual Formly field data
 */
export function applyBranchConditions(clusters: TripettoCluster[], nodeFieldMap: NodeToFieldMap): ConversionWarning[] {
	const warnings: ConversionWarning[] = [];

	for (const cluster of clusters) {
		const clusterWarnings = applyClusterBranches(cluster, nodeFieldMap);
		warnings.push(...clusterWarnings);
	}

	return warnings;
}

/**
 * Apply branch conditions for a single cluster (Pass 2)
 */
function applyClusterBranches(cluster: TripettoCluster, nodeFieldMap: NodeToFieldMap): ConversionWarning[] {
	const warnings: ConversionWarning[] = [];

	// Process branches
	if (cluster.branches && cluster.branches.length > 0) {
		for (const branch of cluster.branches) {
			// Try to convert branch conditions to hideExpression using actual Formly field data
			const hideExpression = convertBranchConditionsToHideExpression(
				branch.conditions,
				nodeFieldMap,
				branch.culling
			);

			// Build description of branch conditions for warnings
			const conditionDescriptions: string[] = [];
			if (branch.conditions && branch.conditions.length > 0) {
				for (const condition of branch.conditions) {
					const condBlock = condition.block;
					if (condBlock) {
						const condType = condBlock.type || 'unknown';
						const nodeId = condBlock.node || 'unknown';
						const condValue = condBlock.match || condBlock.value || condBlock.mode || '';
						conditionDescriptions.push(`${condType} on node ${nodeId.substring(0, 8)}... = ${condValue}`);
					}
				}
			}

			// Process each cluster within this branch
			for (const branchCluster of branch.clusters || []) {
				// Get all fields that were created from this branch cluster
				const branchFields = getAllFieldsFromCluster(branchCluster, nodeFieldMap);

				// Apply hideExpression to each field in this branch
				for (const field of branchFields) {
					if (hideExpression) {
						// Successfully converted conditions to hideExpression
						field.hideExpression = hideExpression;

						// Add info warning to let developer know it was auto-converted
						const conditionInfo =
							conditionDescriptions.length > 0
								? conditionDescriptions.join(' AND ')
								: 'Unknown condition';

						const cullingNote = branch.culling
							? `\nCulling mode: ${branch.culling} (${branch.culling === 'all' ? 'AND' : branch.culling === 'none' ? 'NOT' : 'OR'})`
							: '';

						warnings.push(
							generateWarning(
								'info',
								(field.key as string) || 'unknown',
								field.props?.label || field.props?.name || 'Unnamed Field',
								field.type?.toString() || 'unknown',
								`Field has automatic hideExpression from branch condition`,
								`Condition: ${conditionInfo}${cullingNote}\nGenerated expression: ${hideExpression}`,
								{
									branchConditions: conditionDescriptions,
									branchName: branch.name,
									hideExpression,
									culling: branch.culling
								}
							)
						);
					} else {
						// Could not convert conditions - manual review needed
						const conditionInfo =
							conditionDescriptions.length > 0
								? conditionDescriptions.join(' AND ')
								: 'Unknown condition';

						let suggestion = `This field should only show when: ${conditionInfo}\n\nManually add hideExpression to implement this condition.`;

						// Special message for "each" culling mode
						if (branch.culling === 'each') {
							suggestion = `This field uses Tripetto "each" culling mode (show cluster multiple times, once per matched condition).\n\nFormly does not support duplicating fields based on multiple conditions. Consider:\n1. Restructuring as a repeater/array field\n2. Creating separate fields for each condition\n3. Using custom logic to handle this dynamically\n\nConditions: ${conditionInfo}`;
						}

						warnings.push(
							generateWarning(
								'warning',
								(field.key as string) || 'unknown',
								field.props?.label || field.props?.name || 'Unnamed Field',
								field.type?.toString() || 'unknown',
								`Field is inside a conditional branch but hideExpression could not be auto-generated`,
								suggestion,
								{
									branchConditions: conditionDescriptions,
									branchName: branch.name,
									culling: branch.culling
								}
							)
						);
					}
				}

				// Recursively apply branches for nested clusters
				const nestedWarnings = applyClusterBranches(branchCluster, nodeFieldMap);
				warnings.push(...nestedWarnings);
			}
		}
	}

	return warnings;
}

/**
 * Get all fields that were created from nodes in this cluster and its sub-clusters
 */
function getAllFieldsFromCluster(cluster: TripettoCluster, nodeFieldMap: NodeToFieldMap): FormlyFieldConfig[] {
	const fields: FormlyFieldConfig[] = [];

	// Get fields from this cluster's nodes
	for (const node of cluster.nodes || []) {
		const field = nodeFieldMap.getFieldByNodeId(node.id);
		if (field) {
			fields.push(field);
		}
	}

	// Recursively get fields from branch clusters
	if (cluster.branches && cluster.branches.length > 0) {
		for (const branch of cluster.branches) {
			for (const branchCluster of branch.clusters || []) {
				const branchFields = getAllFieldsFromCluster(branchCluster, nodeFieldMap);
				fields.push(...branchFields);
			}
		}
	}

	return fields;
}
